<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Add Project'" :unsaved="true" />
    <Card class="mx-5 mt-3" pt:body:class="" pt:content:class="p-4!">
      <template #content>
        <Stepper
          :value="activeStep"
          linear
          pt:nav:class="flex justify-center items-center mx-auto p-0 relative list-none xl:w-[60%]"
          pt:panelContainer:class="p-4 bg-surface-50/0!"
        >
          <StepList>
            <Step :value="1">Project Info</Step>
            <Step :value="2">Additional Fields</Step>
            <Step :value="3">Sources Of Funds</Step>
            <Step :value="4">Budget Builder</Step>
          </StepList>
          <StepPanels>
            <!-- ********* PROJECT INFO SECTION ********* -->
            <StepPanel :value="1">
              <form @submit="onSubmit">
                <MaintainProjectInfo
                  v-if="userDetail"
                  v-model:project-settings="projectSettings"
                  v-model:entities="entities"
                  :property-types
                  :property-types-ex
                  :user-groups
                  :checking-accounts
                  :subdivisions
                  :values
                  :permissions="userDetail.permissions"
                  :project-statuses
                  :isDrawZero="true"
                  :set-field-value
                  @user-group-updated="userGroupUpdated"
                ></MaintainProjectInfo>

                <div class="flex pt-4 justify-end">
                  <NextButton type="submit"></NextButton>
                </div>
              </form>
            </StepPanel>

            <!-- ********* Flex FIELDS SECTION ********* -->
            <StepPanel v-slot="{ activateCallback = () => {} }" :value="2" class="bg-transparent">
              <MaintainFlexFields
                v-model:customizeFlexRows="customizeFlexRows"
                v-model:allFields="allFields"
                v-model:flexRowTemplates="flexRowTemplates"
                v-model:selectedFlexRowTemplate="selectedFlexRowTemplate"
                v-model:fields="fields"
                v-model:loadDefaultTemplate="loadDefaultTemplate"
              ></MaintainFlexFields>
              <div class="flex pt-4 justify-between">
                <BackButton
                  @click="
                    activeStep = 1;
                    activateCallback(1);
                  "
                ></BackButton>
                <NextButton @click="onSubmit()"></NextButton>
              </div>
            </StepPanel>

            <!-- ********* SOURCE OF FUNDS SECTION ********* -->
            <StepPanel v-slot="{ activateCallback = () => {} }" :value="3" class="bg-transparent">
              <SourceOfFundsTable
                v-model:project-settings="projectSettings"
                v-model:project-entities="sourceOfFunds"
                v-model:drawItems="drawItems"
                :entities
                :project-info="values"
                :contacts
              ></SourceOfFundsTable>
              <div class="flex pt-4 justify-between">
                <BackButton
                  @click="
                    activeStep = 2;
                    activateCallback(2);
                  "
                ></BackButton>
                <NextButton @click="onSubmit()"></NextButton>
              </div>
            </StepPanel>

            <StepPanel v-slot="{ activateCallback = () => {} }" :value="4" class="bg-transparent">
              <BudgetBuilder
                v-if="userDetail"
                v-model:project-settings="projectSettings"
                v-model:drawItems="drawItems"
                v-model:projectInfo="values"
                v-model:project-entities="sourceOfFunds"
                v-model:project-reminders="projectReminders"
                :permissions="userDetail.permissions"
              ></BudgetBuilder>
              <div class="flex pt-4 justify-between">
                <BackButton
                  @click="
                    activeStep = 3;
                    activateCallback(3);
                  "
                ></BackButton>
                <SubmitButton @click="onSubmit"></SubmitButton>
              </div>
            </StepPanel>
          </StepPanels>
        </Stepper>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { entityAlreadyExistsInProject, entityIsTBD, getCodeTable, getProjectStatuses, getUserGroups } from "@/services/Utility";
import { type ProjectSettings, ProjectInfoWriteSchema } from "@/types/Projects";
import type { Entity } from "@/types/Entity";
import { type UserDetail } from "@/types/User";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { createProjectEntity, type ProjectEntity } from "@/types/ProjectEntity";
import { type Code } from "@/types/Codes";
import { CodeTableNumber } from "@/enums/CodeTable";
import type { DrawItem } from "@/types/DrawItem";
import { type CheckingAccount } from "@/types/CheckingAccount";
import { type FlexRowField, type FlexRowTemplate } from "@/types/FlexRows";
import { getAllFlexFields, getFlexRowTemplates } from "@/services/FlexRowsService";
import { getAllEntities } from "@/services/EntitiesService";
import { getUserDetail } from "@/services/UserService";
import { getCheckingAccounts } from "@/services/PaymentsService";
import type { ProjectReminder } from "@/types/Reminder";
import type { Subdivision } from "@/types/Subdivision";
import { getSubdivisions } from "@/services/SubdivisionService";
import { ProjectRole } from "@/enums/ProjectRole";
import { isClient, NBH, UMB } from "@/services/Client";
import type { ProjectStatus } from "@/types/ProjectStatus";
import { SettingScope } from "@/enums/SystemSettings";

const activeStep = ref<number>(1);

const propertyTypes = ref<Code[]>([]);
const propertyTypesEx = ref<Code[]>([]);
const userGroups = ref<Code[]>([]);
const entities = ref<Entity[]>([]);
const userDetail = ref<UserDetail | undefined>();

const projectStatuses = ref<ProjectStatus[]>([]);

const checkingAccounts = ref<CheckingAccount[]>([]);

const subdivisions = ref<Subdivision[]>([]);

const projectReminders = ref<ProjectReminder[]>([]);

const loadDefaultTemplate = ref(true);

const customizeFlexRows = ref(false);
const allFields = ref<FlexRowField[]>([]);
const flexRowTemplates = ref<FlexRowTemplate[]>([]);
const selectedFlexRowTemplate = ref<FlexRowTemplate>();
const fields = ref<FlexRowField[]>([]);

const sourceOfFunds = ref<ProjectEntity[]>([]);
const contacts = ref<ProjectEntity[]>([]);

const router = useRouter();

//TODO: 4 decimal places for currency custom fields?
//TODO: allow ability to change edit lists from dropdown like with the entity lookups

let initialValues: any = {
  projectID: "",
  projectCode: "",
  projectName: "",
  propertyType: null,
  propertyTypeEx: null,
  projectStatus: null,
  checkingAccount: null,
  subdivision: null,
  processor: null,
  lender: null,
  borrower: null,
  builder: null,
  inspector: null,
  userGroup: null,
  address: "",
  county: "",
  city: "",
  state: "",
  zip: "",
  projectNotes: "",
  specialInstructions: "",
  defaultPayAction: isClient(NBH) ? "Fed Wire" : "Checks",
  hasRetainage: false,
  sendProcessorCompletionEmail: false,
  sendLenderCompletionEmail: false,
  sendBorrowerCompletionEmail: false,
  sendBuilderCompletionEmail: false,
  sendInspectorCompletionEmail: false,
  soldDate: null,
};

const { values, setFieldValue, handleSubmit } = useForm({
  validationSchema: toTypedSchema(ProjectInfoWriteSchema),
  initialValues: initialValues,
  keepValuesOnUnmount: true,
});

const projectSettings = ref<ProjectSettings>({
  linearNumbering: true,
  enablePercentageBudget: false,
  autoDisburseFunds: true,
  enableLenderParticipation: false,
  participationMethod: "PERCENT",
  enableEquityParticipation: false,
  equityParticipationMethod: "PERCENT",
  enableRevolvingCredit: false,
  hasImportedDraws: false,
  hasImportedInspections: false,
  budgetColumns: null,
  enableEquityPayments: false,
  showBorrowerName: isClient(UMB) ? true : false,
  budgetColumnOrder: [],
  condensedBudgetColumns: null,
  sourcesColumnOrder: [],
  automaticDefaultPayments: false,
});

const drawItems = ref<DrawItem[]>([]);

const userGroupUpdated = (): void => {
  setFieldValue("checkingAccount", null);
  setFieldValue("subdivision", null);
  checkingAccounts.value = [];
  subdivisions.value = [];
  updateCheckingAccounts();
  updateSubdivisions();
};

const getEntities = (): void => {
  getAllEntities().then((result: Entity[]) => {
    entities.value = result;
    const tbdEntity = entities.value.find((entity: Entity) => entityIsTBD(entity));
    const userEntity = entities.value.find((entity: Entity) => entity.entityId === userDetail.value?.entityId);
    const primaryLender = entities.value.find((entity: Entity) => entity.primaryLender);
    if (tbdEntity) {
      setFieldValue("processor", userEntity != null ? userEntity : tbdEntity);
      setFieldValue("lender", primaryLender != null ? primaryLender : tbdEntity);
      setFieldValue("borrower", tbdEntity);
      setFieldValue("builder", tbdEntity);
      setFieldValue("inspector", tbdEntity);
    }
  });
};

const updateCheckingAccounts = async (): Promise<void> => {
  getCheckingAccounts(values.userGroup, true).then((result: CheckingAccount[]) => {
    checkingAccounts.value = result;
  });
};

const updateSubdivisions = async (): Promise<void> => {
  getSubdivisions(values.userGroup).then((result: Subdivision[]) => {
    subdivisions.value = result;
  });
};

const getDefaultBudgetColumns = (): void => {
  axios
    .get("/settings/columns/templates/default/" + SettingScope.BUDGET_COLUMN_TEMPLATES)
    .then((response) =>
      handleApiResponse(
        response,
        (result: number | null) => {
          projectSettings.value.budgetColumns = result;
        },
        (errors: ApiError[]) => {
          error("Cannot save budget", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

onBeforeMount(async (): Promise<void> => {
  await getUserDetail().then((result: UserDetail | null) => {
    if (result == null) return;

    //If user is part of the all group then they need to select which group the project goes in
    userDetail.value = result;
    if (!userDetail.value.permissions.addProject) {
      error("Action Restricted", "You are not authorized to add projects.");
      router.back();
      return;
    }

    getUserGroups(true, true).then((result: Code[]) => {
      userGroups.value = result;
      if (userGroups.value.length == 1) setFieldValue("userGroup", userGroups.value[0]);
    });
  });

  getEntities();
  getCodeTable(CodeTableNumber.PROPERTY_TYPES).then((result: Code[]) => (propertyTypes.value = result));
  getCodeTable(CodeTableNumber.PROPERTY_TYPES_EX).then((result: Code[]) => (propertyTypesEx.value = result));
  updateCheckingAccounts();
  updateSubdivisions();
  getAllFlexFields().then((result: FlexRowField[]) => (allFields.value = result));
  getFlexRowTemplates().then((result: FlexRowTemplate[]) => (flexRowTemplates.value = result));
  getDefaultBudgetColumns();
  getProjectStatuses().then((result: ProjectStatus[]) => {
    projectStatuses.value = result;
    let pendingStatus = projectStatuses.value.find((status) => status.name === "PENDING");
    setFieldValue("projectStatus", pendingStatus);
  });
});

const onSubmit = handleSubmit((values: any): void => {
  if (activeStep.value == 1) {
    if (entityAlreadyExistsInProject(values, sourceOfFunds.value, contacts.value, values.processor, ProjectRole.PROCESSOR)) return;
    if (entityAlreadyExistsInProject(values, sourceOfFunds.value, contacts.value, values.lender, ProjectRole.PRIMARY_LENDER)) return;
    if (entityAlreadyExistsInProject(values, sourceOfFunds.value, contacts.value, values.borrower, ProjectRole.PRIMARY_BORROWER)) return;
    if (entityAlreadyExistsInProject(values, sourceOfFunds.value, contacts.value, values.builder, ProjectRole.PRIMARY_BUILDER)) return;
    if (entityAlreadyExistsInProject(values, sourceOfFunds.value, contacts.value, values.inspector, ProjectRole.PRIMARY_INSPECTOR)) return;

    if (sourceOfFunds.value.length == 0) {
      sourceOfFunds.value[0] = createProjectEntity("PRIMARY BORROWER", values.borrower, 1, 0, "");
      sourceOfFunds.value[0].participationPercent = 100;
      sourceOfFunds.value[1] = createProjectEntity("PRIMARY LENDER", values.lender, 2, 0, "");
      sourceOfFunds.value[1].participationPercent = 100;
    } else {
      const borrower = sourceOfFunds.value.find((entity) => entity.type === "PRIMARY BORROWER");
      if (borrower) borrower.entity = values.borrower;

      const lender = sourceOfFunds.value.find((entity) => entity.type === "PRIMARY LENDER");
      if (lender) lender.entity = values.lender;
    }
    activeStep.value++;
  } else if (activeStep.value == 2) {
    activeStep.value++;
  } else if (activeStep.value == 3) {
    let totalBorrowerPercent = 100;
    let totalLenderPercent = 100;
    if (projectSettings.value.enableEquityParticipation && projectSettings.value.equityParticipationMethod === "PERCENT") {
      const borrowers = sourceOfFunds.value.filter((source) => source.type.toUpperCase().includes("BORROWER"));
      totalBorrowerPercent = borrowers.reduce((accumulator, entity) => accumulator + entity.participationPercent, 0);
    }
    if (projectSettings.value.enableLenderParticipation && projectSettings.value.participationMethod === "PERCENT") {
      const lenders = sourceOfFunds.value.filter((source) => source.type.toUpperCase().includes("LENDER"));
      totalLenderPercent = lenders.reduce((accumulator, entity) => accumulator + entity.participationPercent, 0);
    }
    if (totalBorrowerPercent !== 100) {
      error("Error", "Borrower splits are not totaling 100%");
    } else if (totalLenderPercent !== 100) {
      error("Error", "Lender splits are not totaling 100%");
    } else {
      activeStep.value++;
    }
  } else if (activeStep.value == 4) {
    axios
      .post("/projects/add", {
        projectInfo: values,
        projectSettings: projectSettings.value,
        items: drawItems.value,
        sourceOfFunds: sourceOfFunds.value,
        flexFields: fields.value,
      })
      .then((response) => {
        router.push({
          name: "project-dashboard",
          params: {
            id: response.data.result,
          },
        });
      })
      .catch((e) => errorApi(e));
  }
});
</script>
